import { ENV_VARS } from '@/env';
import { setQuoteModalBlocker } from '@/redux/common/CommonActions';
import { Col, Form, Row } from 'antd';
import { memo, useMemo, useState } from 'react';
import Files from 'react-files';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReactComponent as DownloadIcon } from '../../../../../../assets/icons/download-white.svg';
import { MODULES_ID } from '../../../../../../contants/common';
import { valueToString } from '../../../../../../utilz';
import { routeFileSelector } from '../../../../../../utilz/i18n';
import Button from '../../../../../components/Buttons/Button';
import IconButton from '../../../../../components/Buttons/IconButton';
import SvgConverter from '../../../../../components/Common/SvgConverter';
import FormInput from '../../../../../components/Inputs/FormInput';
import HiddenFormInput from '../../../../../components/Inputs/HiddenFormInput';
import SecondaryLabel from '../../../../../components/Labels/SecondaryLabel';
import Paper from '../../../../../components/Paper/Paper';
import DocumentTypeSelector from '../../../../../components/Selectors/DocumentTypeSelector';
import HallFiledCard from '../../HallFiledCard';
import SaveCancelButtons from '../../SaveCancelButtons';
import { docTypeCodeRules, fileRules } from './constants';

const isHalliburton = ENV_VARS.REACT_APP_THEME === 'halliburton';
const buttonClassName = isHalliburton ? 'hb-button' : '';

const HallFilesDetailsCard = ({
  isNew,
  fileDetails,
  handleSave,
  handleDelete,
  index,
  allowedExtentions,
  handleSaveForAddNewScreen,
  isPartialSave,
  hideEditButton,
  isDocumentButtonsEnabled,
  isCanAddFile = true,
  hideDeleteButton,
}) => {
  const [editMode, setEditMode] = useState(Boolean(isNew) && isCanAddFile);
  const [file, setFile] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [form] = Form.useForm();

  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const handleSaveButton = async () => {
    try {
      await form.validateFields();
      const data = form.getFieldsValue();
      setIsModified(false);

      if (handleSaveForAddNewScreen) {
        handleSaveForAddNewScreen(data, file, index);
      } else {
        const formData = new FormData();
        formData.set('DocumentTitle', valueToString(data.documentTitle));
        formData.set('ReferenceNo', valueToString(data.referenceNo));
        formData.set('DocumentTypeCode', data.docTypeCode);
        formData.set('DocumentDesc', data.documentDesc);
        if (file) {
          formData.set('File', file);
        }

        handleSave({ ...data, formData }, isNew, index);
      }
      setEditMode(false);
    } catch {}
  };

  const handleFileChange = (files) => {
    try {
      if (files.length > 0) {
        setFile(files[0]);
        form.setFieldsValue({ file: files[0].name });
        if (!isModified) {
          setIsModified(true);
        }
      }
    } catch {}
  };

  const handleFileError = (error) => {
    try {
      if (error) {
        form.setFields([
          {
            name: 'file',
            errors: [error.message],
          },
        ]);
      }
    } catch {}
  };

  const handleReset = () => {
    setIsModified(false);
    form.setFieldsValue({
      docTypeCode: fileDetails.fileType,
      referenceNo: fileDetails.referenceNo,
      documentTitle: fileDetails.fileComments,
      documentDesc: fileDetails.fileDescription,
      carrierName: fileDetails.carrierName,
    });
    setEditMode(false);
    dispatch(setQuoteModalBlocker(false));
  };

  const fileName = Form.useWatch('file', form);
  const documentDesc = Form.useWatch('documentDesc', form);
  const dispatch = useDispatch();
  const quoteModalBlocker = useSelector((state) => state.common.quoteModalBlocker);

  return (
    <Form
      form={form}
      initialValues={
        fileDetails?.fileGuid
          ? {
              docTypeCode: fileDetails.fileType,
              referenceNo: fileDetails.referenceNo,
              documentTitle: fileDetails.fileComments,
              documentDesc: fileDetails.fileDescription,
              file: fileDetails.file,
              carrierName: fileDetails.carrierName,
            }
          : {}
      }
      onFieldsChange={() => {
        setIsModified(true);
        if (!quoteModalBlocker) {
          dispatch(setQuoteModalBlocker(true));
        }
      }}
    >
      <Paper className="border-muted mb-16" dataTestId="document-details-files-card">
        <div className="border-bottom-muted px-16 pt-8 pb-10">
          <Row justify="space-between" align="middle">
            <Col>
              <Row gutter={5} align="middle">
                <Col>
                  <SecondaryLabel className="f-12">{t('translation:default.fields.fileName')}:</SecondaryLabel>
                </Col>

                <Col>
                  {editMode && allowedExtentions && (
                    <Files
                      className="files-dropzone"
                      onChange={handleFileChange}
                      onError={handleFileError}
                      accepts={allowedExtentions}
                      multiple={false}
                      minFileSize={0}
                      clickable
                    >
                      <Button dataTestId="document-manager-choose-file-button" className={buttonClassName}>
                        {t('translation:default.documentManager.chooseFile')}
                      </Button>
                    </Files>
                  )}
                  <HiddenFormInput name="file" rules={fileRules(t)} />
                </Col>

                <Col>
                  {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
                  <label className="f-14 fw-600">{fileName}</label>
                </Col>
              </Row>
            </Col>
            <Col>
              <Row gutter={16} align="middle">
                {fileDetails?.cloudUrl?.length > 0 && (
                  <Col>
                    <a href={fileDetails?.cloudUrl?.[0]} rel="noreferrer">
                      <IconButton dataTestId="file-detail-card-download-button">
                        <SvgConverter Icon={DownloadIcon} color="header" />
                      </IconButton>
                    </a>
                  </Col>
                )}

                <Col className="lh-0">
                  <SaveCancelButtons
                    editMode={editMode}
                    hideDeleteButton={hideDeleteButton}
                    disableCancelOnHiddenDelete={false}
                    hideEditButton={hideEditButton}
                    shipmentEditStatus={!isDocumentButtonsEnabled}
                    setEditMode={setEditMode}
                    onCancel={() => {
                      if (isNew) {
                        handleDelete(index, isNew);
                      } else {
                        handleReset();
                      }
                    }}
                    handleRemove={() => handleDelete(index, isNew)}
                    isNew={isNew}
                    isModified={isModified}
                    saveButtonType="button"
                    onSave={handleSaveButton}
                    isUseBlocker={false}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
        <Row className="p-16" gutter={16}>
          <Col sm={6}>
            <Row>
              <Col sm={24}>
                {editMode ? (
                  <DocumentTypeSelector
                    editMode={editMode ? Boolean(isNew) : false}
                    margin="mb-16"
                    name="docTypeCode"
                    moduleId={MODULES_ID.SHIPMENT}
                    rules={docTypeCodeRules(t)}
                    onChange={({ label }) => form.setFieldsValue({ documentDesc: label })}
                    disableValidation={isPartialSave}
                  />
                ) : (
                  <HallFiledCard
                    containerClass="mt-0"
                    isRequired
                    title={t('translation:default.documentManager.documentManager.label')}
                  >
                    {documentDesc}
                  </HallFiledCard>
                )}
                <HiddenFormInput name="docTypeCode" />
              </Col>
            </Row>
          </Col>
          <Col sm={6}>
            <Row>
              <Col sm={24}>
                <FormInput
                  label={t('translation:shipmentAdd.documentDetails.supportingDocument.label')}
                  editMode={editMode}
                  margin="mb-0"
                  name="referenceNo"
                  maxLength={50}
                  dataTestId="file-details-card-reference-no"
                />
              </Col>
            </Row>
          </Col>
          <Col sm={6}>
            <Row>
              <Col sm={24}>
                <FormInput
                  label={t('translation:default.fields.transportationProvider.label')}
                  editMode={false}
                  margin="mb-0"
                  name="carrierName"
                  dataTestId="file-details-card-carrier-name"
                />
              </Col>
            </Row>
          </Col>
          <Col sm={6}>
            <Row>
              <Col sm={24}>
                <FormInput
                  label={t('translation:default.documentManager.fileComments.label')}
                  editMode={editMode}
                  margin="mb-0"
                  name="documentTitle"
                  dataTestId="file-details-card-document-title"
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Paper>
      <HiddenFormInput name="documentDesc" />
    </Form>
  );
};

export default memo(HallFilesDetailsCard);
